// ModernPanelDragShell — Modern パネルをドラッグ並べ替え/ドック間移動できるようにする
// 汎用ラッパ (§9.10 決定1 C「ドック内並べ替え + パネル間移動(ドラッグ)」、第4スライス).
//
// 背景 (docs/_wip/modern-layout.md §9.10 第4スライス / 第1スライス残課題):
//   - 第1〜3スライスの renderRegion は region 別ラッパ (className) を switch でハードコード
//     していた。C (region をまたぐ移動) を入れるには「どのパネルがどの region に来ても
//     描ける」汎用ラッパが要る (= 残課題)。本コンポーネントがその汎用ラッパ。
//   - **ドラッグ affordance**: パネルヘッダ上端に薄い「グリップ帯」を出し、そこを掴んで
//     ドラッグ起点にする。パネル全体を draggable にするとヘッダ内のボタン/スライダの
//     誤操作を招くので、**グリップ帯だけ** をドラッグ起点にする (= 安全な掴み所)。
//   - **ドロップ位置インジケータ**: ドラッグ中、このシェルが drop ターゲットのとき上端 or
//     下端に挿入線 (2px のアクセント線) を出す。挿入位置は親 (ModernLayout) が一元的に
//     ヒットテストして決める (下記参照)。
//
// ## DnD 方式 (S1、2026-08-01: HTML5 DnD → pointer events へ全面移行)
//
// 旧実装はネイティブ HTML5 drag-and-drop (`draggable` + `dragstart`/`dragover`/`drop`)
// だった。floating-panels-2026-08-01.md §3 決定2 により **pointer events
// (`pointerdown` + `setPointerCapture` + 数 px 移動閾値) の 1 系統へ全面移行**した:
//
//   - `setPointerCapture` で掴んだ grip 要素に以後の pointermove/up/cancel を集約する
//     (= 要素外・画面外へポインタが出ても追従を切らさない。`_archive/classic-ui-restore.md`
//     R10 で確立した流儀、`ArgbSliderPanel.tsx` の `handleDown`/`handleMove`/`handleUp` と
//     同型)。これにより native DnD の drag ghost は無くなるが、ドラッグ中の視覚表現は現状の
//     `opacity-40` を維持する (= 挙動不変、追従プレビューは S2 で足す)。
//   - pointer capture 中は「今どのパネル/region の上にいるか」をブラウザの dragover が
//     教えてくれない (= 要素の上を素通りしても pointermove は grip 自身に来る) ので、
//     ヒットテストは親 (`ModernLayout`) が一元的に行う: 各パネルシェルは `data-panel-id` /
//     `data-region` / `data-index` を DOM に出すだけで、`onDragOver`/`onDrop` は持たない
//     (= 純ロジック `panelDragGeometry.ts` の `resolvePanelDropTarget` が矩形集合 + ポインタ
//     座標から before/after を判定する、S1 §9 落とし穴「純ロジックは DOM 非依存の純関数に
//     切り出す」に対応)。
//   - ドラッグ開始判定 (= 数 px 閾値未満はクリック扱い) とドラッグ終了 (pointerup =
//     確定 / pointercancel・Escape = キャンセル) はこのシェルが担い、親には
//     `onPointerDragStart` / `onPointerDragMove` / `onPointerDragEnd` の 3 コールバックで
//     通知する (= 親は「今どのパネルが動いているか」「今どこにいるか」「確定したか」だけ
//     知ればよい、判定ロジックの詳細を持ち込まない)。
//
// engine/protocol には触れない。
//
// ## S2 (floating-panels-2026-08-01.md §2、2026-08-01): グリップ配管を共有 hook へ抽出
//
// pointer-capture のグリップ配管 (pointerdown→閾値判定→setPointerCapture→pointermove→
// pointerup/cancel、Escape キャンセル) は `usePanelDragGrip` (`layouts/shared/`) へ抽出した。
// S2 で新設したフロートパネル (`FloatShell`) も同じ hook を使う (= 「フロートパネルも同じ
// グリップ・同じ drag 経路を通ること (2 系統作らない)」§2 の要請)。本コンポーネントの見た目・
// 挙動 (グリップ帯 / 挿入線 / opacity-40) は無改変、配管の抽出のみ。

import type React from 'react';

import type { FloatPoint } from '../shared/floatShellGeometry.js';
import { usePanelDragGrip } from '../shared/usePanelDragGrip.js';
import { GripIcon } from './ModernIcons.js';
import type { PanelId, WorkspaceRegion } from './workspaceLayout.js';

/** 現在のドロップ候補位置 (= どの region の どの挿入 index にドロップするか)。 */
export interface DropTarget {
  readonly region: WorkspaceRegion;
  /** 挿入 index (= movePanel の toIndex)。0 = 先頭, len = 末尾。 */
  readonly index: number;
}

export interface ModernPanelDragShellProps {
  /** このシェルが包むパネル。 */
  readonly panelId: PanelId;
  /** このパネルが属する region。 */
  readonly region: WorkspaceRegion;
  /** region 内でのこのパネルの index (= 並べ替え基準位置)。 */
  readonly index: number;
  /** パネル表示名 (= グリップ帯 aria ラベル用)。 */
  readonly label: string;
  /** 包むパネル本体 (= panelRegistry[id]())。 */
  readonly children: React.ReactNode;
  /**
   * このパネルが列内で残り高さを占有するか (= toolRail / socialDock)。true なら shell と
   * 本体ラッパに `flex-1 min-h-0` を付け、本体直下の要素を全高に伸ばす (= 移設前の
   * `flex flex-1 flex-col [&>div]:h-full` / socialDock の自前 flex-1 と同じ伸び方)。
   * false (既定) なら自然高さ (= shrink しない固定ブロック)。
   */
  readonly grow?: boolean;
  /** 今ドラッグ中のパネル (= 自分なら掴まれている。null ならドラッグ無し)。 */
  readonly draggingId: PanelId | null;
  /** 現在のドロップ候補 (= 自分が before/after ターゲットならインジケータを出す)。 */
  readonly dropTarget: DropTarget | null;
  /**
   * ドラッグ開始 (= grip の pointerdown から閾値を超えて動いた瞬間、一度だけ)。`grabOffset`
   * は「掴んだ点 − このシェル (`[data-panel-id]`) の rect 左上」の実測値 (§11「掴んだ位置を
   * 保持する」)。
   */
  readonly onPointerDragStart: (panelId: PanelId, grabOffset: FloatPoint) => void;
  /**
   * ドラッグ中の pointermove (= client 座標を都度渡す)。親がこの座標で一元的に
   * ヒットテストして dropTarget / レールスロットを更新する (このシェルは判定しない)。
   */
  readonly onPointerDragMove: (clientX: number, clientY: number) => void;
  /**
   * ドラッグ終了。`committed=true` (pointerup = 確定) なら親が現 dropTarget へ移動を
   * 適用し、`false` (pointercancel / Escape = キャンセル) なら何もせず状態だけ戻す。
   */
  readonly onPointerDragEnd: (committed: boolean) => void;
}

/**
 * パネル 1 枚をドラッグ可能なシェルで包む。グリップ帯 (掴み所) + ドロップ挿入線 +
 * ドラッグ中の見た目 (掴まれているパネルは半透明) を担う。
 */
export const ModernPanelDragShell = ({
  panelId,
  region,
  index,
  label,
  children,
  grow = false,
  draggingId,
  dropTarget,
  onPointerDragStart,
  onPointerDragMove,
  onPointerDragEnd,
}: ModernPanelDragShellProps): React.ReactNode => {
  const isDragging = draggingId === panelId;
  // 自分が現 dropTarget か (= region 一致 + index が自分の before(=index) か after(=index+1))。
  const isBeforeTarget =
    dropTarget !== null && dropTarget.region === region && dropTarget.index === index;
  const isAfterTarget =
    dropTarget !== null && dropTarget.region === region && dropTarget.index === index + 1;

  // pointer-capture グリップ配管 (S2 で共有 hook へ抽出、上記コメント参照)。
  // measureGrabOffset: 掴んだ点とこのシェル (`[data-panel-id]` を持つ root div) 左上の
  // 相対距離を計測する (§11「ドック内パネル (detach) は `[data-panel-id]` 要素の rect が
  // 基準」)。grip 要素自身 (上端の細い帯) ではなく root を基準にする — フロート化後の
  // `FloatShell` はパネル全体を掴み所にするため、detach 前後で基準がズレないようにする。
  const grip = usePanelDragGrip({
    onDragStart: (grabOffset) => onPointerDragStart(panelId, grabOffset),
    onDragMove: onPointerDragMove,
    onDragEnd: onPointerDragEnd,
    measureGrabOffset: (e) => {
      const root = (e.currentTarget as HTMLElement).closest<HTMLElement>('[data-panel-id]');
      const rect = root?.getBoundingClientRect();
      if (!rect) return { x: 0, y: 0 };
      return { x: e.clientX - rect.left, y: e.clientY - rect.top };
    },
  });

  return (
    <div
      data-panel-id={panelId}
      data-region={region}
      data-index={index}
      className={`relative flex flex-col ${grow ? 'min-h-0 flex-1' : 'shrink-0'} ${
        isDragging ? 'opacity-40' : ''
      }`}
    >
      {/* before インジケータ (= このパネルの直前へ挿入する挿入線)。 */}
      {isBeforeTarget && (
        <div
          data-testid="modern-drop-indicator-before"
          className="pointer-events-none absolute inset-x-0 -top-1 z-10 h-[2px] rounded-full bg-[var(--color-accent)]"
        />
      )}
      {/* グリップ帯 (= 掴み所)。ここだけが pointer drag の起点。ヘッダ内ボタン/スライダの
          誤操作を避けるためパネル全体ではなくこの帯を起点にする。`touch-none`: タッチで
          掴んだときページスクロールと競合しないよう `touch-action: none` を当てる
          (pointer events 化の副産物としてタッチドラッグも成立するようになったため)。 */}
      {/* biome-ignore lint/a11y/useSemanticElements: ドラッグハンドル (= pointer drag の掴み所)。role=button は aria-label のための器で実際の操作は pointerdown+move+up (キーボードでの並べ替えは未対応、復帰経路は ModernPanelVisibilityMenu が担保)。Escape はドラッグ開始後のみ window keydown で拾いキャンセルする. */}
      <div
        draggable={false}
        data-testid={`modern-panel-grip-${panelId}`}
        role="button"
        tabIndex={0}
        aria-label={`${label} を移動 (ドラッグ)`}
        title={`${label} を掴んで並べ替え / 別ドックへ移動`}
        onPointerDown={grip.onPointerDown}
        onPointerMove={grip.onPointerMove}
        onPointerUp={grip.onPointerUp}
        onPointerCancel={grip.onPointerCancel}
        onLostPointerCapture={grip.onLostPointerCapture}
        className="flex cursor-grab touch-none select-none items-center justify-center rounded-t bg-[var(--color-bg-soft)] py-[1px] text-[var(--color-text-muted)] opacity-50 transition-opacity hover:opacity-100 active:cursor-grabbing"
      >
        <GripIcon className="h-2.5 w-4" />
      </div>
      {/* パネル本体 (= panelRegistry の render 結果。中身は無改変)。grow パネルは本体ラッパも
          flex-1 min-h-0 にし、本体直下 (= ToolRail のルート枠) を全高に伸ばす ([&>div]:h-full)。 */}
      <div className={`flex min-h-0 flex-col ${grow ? 'flex-1 [&>div]:h-full' : ''}`}>
        {children}
      </div>
      {/* after インジケータ (= このパネルの直後へ挿入する挿入線)。 */}
      {isAfterTarget && (
        <div
          data-testid="modern-drop-indicator-after"
          className="pointer-events-none absolute inset-x-0 -bottom-1 z-10 h-[2px] rounded-full bg-[var(--color-accent)]"
        />
      )}
    </div>
  );
};
